<template>
  <div class="order-ahead-drawer-panel q-pa-sm">
    <div class="row items-center justify-between no-wrap q-gutter-xs q-mb-md">
      <div class="text-h6 ellipsis col col-grow order-ahead-drawer-title">{{ t('layout.orderAheadPickup') }}</div>
      <div class="row items-center no-wrap q-gutter-xs">
        <q-btn
          flat
          dense
          round
          icon="close"
          :aria-label="t('layout.close')"
          @click="emit('close')"
        >
          <q-tooltip>{{ t('layout.close') }}</q-tooltip>
        </q-btn>
        <q-btn flat dense round icon="refresh" :loading="store.loading" :aria-label="t('dashboard.refresh')" @click="store.fetchQueue()">
          <q-tooltip>{{ t('dashboard.refresh') }}</q-tooltip>
        </q-btn>
        <q-btn
          flat
          dense
          color="primary"
          no-caps
          :round="drawerActionsIconOnly"
          :icon="drawerActionsIconOnly ? 'open_in_full' : undefined"
          :label="drawerActionsIconOnly ? undefined : t('layout.orderAheadOpenFull')"
          @click="goKitchen"
        >
          <q-tooltip v-if="drawerActionsIconOnly">{{ t('layout.orderAheadOpenFull') }}</q-tooltip>
        </q-btn>
      </div>
    </div>

    <q-banner v-if="store.error" dense class="bg-negative text-white q-mb-sm" rounded>{{ store.error }}</q-banner>

    <div v-if="!store.sales.length && !store.loading" class="text-body2 text-grey-7">
      {{ t('kds.noOrders') }}
    </div>

    <div class="column q-gutter-md">
      <OrderAheadSaleCard
        v-for="sale in store.sales"
        :key="sale.id"
        :sale="sale"
        compact
        :saving="savingSaleId === sale.id"
        :feedback-type="feedbackBySaleId[sale.id] || null"
        :cancel-grace-active="isCancelGraceActive(sale.id)"
        :cancel-seconds-left="cancelSecondsLeft(sale.id)"
        :relative-time="formatRelativeTime(sale.created_at)"
        :absolute-time="formatDate(sale.created_at)"
        @advance="(next) => advanceStatus(sale, next)"
        @request-cancel="requestCancel(sale)"
        @undo-cancel="undoCancel(sale.id)"
        @print="printForSale(sale)"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useQuasar } from 'quasar'
import { useBluDialog } from '@/composables/useBluDialog'
import { useI18n } from 'vue-i18n'
import { useOrderAheadQueueStore } from '@/stores/orderAheadQueue'
import OrderAheadSaleCard from '@/components/orderAhead/OrderAheadSaleCard.vue'
import { printKdsTicket } from '@/composables/useKdsQueuePrint'

const { t, te, locale } = useI18n()
const $q = useQuasar()
const { confirm: bluDialog } = useBluDialog()
const router = useRouter()
const store = useOrderAheadQueueStore()

const emit = defineEmits(['close'])

const drawerActionsIconOnly = computed(() => $q.screen.lt.sm)

const savingSaleId = ref(null)
const feedbackBySaleId = reactive({})
const cancelTimers = new Map()
const cancelDeadlineBySaleId = reactive({})
const timeTick = ref(0)

let cancelTickTimer = null

const CANCEL_GRACE_MS = 5500
const FEEDBACK_DWELL_MS = 450

function goKitchen () {
  router.push({ name: 'kitchen-queue' })
}

function fulfillmentStatusLabel (status) {
  void locale.value
  if (!status) return ''
  const path = `kds.fulfillment.${status}`
  return te(path) ? t(path) : status
}

function formatDate (d) {
  if (!d) return ''
  try {
    return new Date(d).toLocaleString()
  } catch {
    return String(d)
  }
}

function formatRelativeTime (d) {
  void timeTick.value
  void locale.value
  if (!d) return ''
  try {
    const ts = new Date(d).getTime()
    const diff = Date.now() - ts
    const sec = Math.floor(diff / 1000)
    if (sec < 45) return t('kds.relativeJustNow')
    const mins = Math.floor(sec / 60)
    if (mins < 60) return t('kds.relativeMinutesAgo', { n: mins })
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return t('kds.relativeHoursAgo', { n: hrs })
    const days = Math.floor(hrs / 24)
    return days === 1 ? t('kds.relativeOneDayAgo') : t('kds.relativeDaysAgo', { n: days })
  } catch {
    return ''
  }
}

function cancelSecondsLeft (saleId) {
  void timeTick.value
  const end = cancelDeadlineBySaleId[saleId]
  if (!end) return 0
  return Math.max(0, Math.ceil((end - Date.now()) / 1000))
}

function isCancelGraceActive (saleId) {
  void timeTick.value
  const end = cancelDeadlineBySaleId[saleId]
  if (!end) return false
  return Date.now() < end
}

async function advanceStatus (sale, next) {
  const fid = sale.id
  const feedbackType =
    next === 'in_progress' ? 'started' : next === 'ready' ? 'ready' : next === 'completed' ? 'complete' : null
  if (feedbackType) {
    feedbackBySaleId[fid] = feedbackType
  }
  savingSaleId.value = sale.id
  try {
    await store.patchFulfillment(sale.id, next)
    await new Promise((r) => setTimeout(r, FEEDBACK_DWELL_MS))
    await store.fetchQueue()
  } catch (e) {
    delete feedbackBySaleId[fid]
    $q.notify({
      type: 'negative',
      message: e?.response?.data?.message || e?.response?.data?.error || e?.message || t('kds.updateFailed')
    })
  } finally {
    savingSaleId.value = null
    delete feedbackBySaleId[fid]
  }
}

function requestCancel (sale) {
  bluDialog({
    title: t('kds.dialogCancelTitle'),
    message: t('kds.dialogCancelMessage', { id: sale.id }),
    cancel: { label: t('kds.dialogKeep'), flat: true, color: 'primary' },
    ok: { label: t('kds.dialogConfirmCancel'), color: 'negative', unelevated: true },
    persistent: true
  }).onOk(() => {
    cancelDeadlineBySaleId[sale.id] = Date.now() + CANCEL_GRACE_MS
    const timer = window.setTimeout(() => {
      finalizeCancel(sale.id)
    }, CANCEL_GRACE_MS)
    cancelTimers.set(sale.id, timer)
  })
}

function undoCancel (saleId) {
  const timer = cancelTimers.get(saleId)
  if (timer) window.clearTimeout(timer)
  cancelTimers.delete(saleId)
  delete cancelDeadlineBySaleId[saleId]
}

async function finalizeCancel (saleId) {
  cancelTimers.delete(saleId)
  if (!cancelDeadlineBySaleId[saleId]) {
    return
  }
  delete cancelDeadlineBySaleId[saleId]
  savingSaleId.value = saleId
  try {
    await store.patchFulfillment(saleId, 'cancelled')
    await store.fetchQueue()
  } catch (e) {
    $q.notify({
      type: 'negative',
      message: e?.response?.data?.message || e?.response?.data?.error || e?.message || t('kds.cancelFailed')
    })
  } finally {
    savingSaleId.value = null
  }
}

function printForSale (sale) {
  printKdsTicket(sale, { t, fulfillmentStatusLabel, formatDate })
}

onMounted(() => {
  cancelTickTimer = window.setInterval(() => {
    timeTick.value = Date.now()
  }, 500)
})

onUnmounted(() => {
  if (cancelTickTimer) clearInterval(cancelTickTimer)
  cancelTimers.forEach((timer) => window.clearTimeout(timer))
  cancelTimers.clear()
})
</script>

<style scoped>
.order-ahead-drawer-title {
  min-width: 0;
}
</style>
